import { fetchTeamSubscriptionUsers } from '../../services/admin/subscriptionTrackerApi';
import { TeamUserSubscriptionListResponse } from '../../types/admin/subscriptionTracker';

const HOVER_FETCH_LIMIT = 200;

export type TeamSeatMembersState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; data: TeamUserSubscriptionListResponse }
  | { status: 'error'; error: string };

type CacheEntry =
  | { status: 'loading'; promise: Promise<TeamSeatMembersState> }
  | { status: 'success'; data: TeamUserSubscriptionListResponse };

/** Session/page cache so repeated hovers for the same team do not refetch. */
const seatMembersCache = new Map<number, CacheEntry>();

function toState(entry: CacheEntry): TeamSeatMembersState {
  if (entry.status === 'loading') return { status: 'loading' };
  return { status: 'success', data: entry.data };
}

/** Snapshot of cached state without starting a fetch. */
export function peekTeamSeatMembers(teamId: number): TeamSeatMembersState {
  const entry = seatMembersCache.get(teamId);
  return entry ? toState(entry) : { status: 'idle' };
}

/**
 * On-demand fetch of seated team members (limit=200, no search).
 * Caches successful responses per team_id. Optional signal aborts the HTTP call.
 */
export function loadTeamSeatMembers(
  teamId: number,
  signal?: AbortSignal
): Promise<TeamSeatMembersState> {
  const existing = seatMembersCache.get(teamId);
  if (existing?.status === 'success') {
    return Promise.resolve({ status: 'success', data: existing.data });
  }
  if (existing?.status === 'loading' && !signal) {
    return existing.promise;
  }

  const promise = (async (): Promise<TeamSeatMembersState> => {
    const result = await fetchTeamSubscriptionUsers(
      teamId,
      { limit: HOVER_FETCH_LIMIT, offset: 0 },
      signal
    );
    if (result.aborted) {
      const current = seatMembersCache.get(teamId);
      if (current?.status === 'loading') seatMembersCache.delete(teamId);
      return { status: 'error', error: 'aborted' };
    }
    if (result.data) {
      seatMembersCache.set(teamId, { status: 'success', data: result.data });
      return { status: 'success', data: result.data };
    }
    const error = result.error ?? 'Failed to load seat members';
    seatMembersCache.delete(teamId);
    return { status: 'error', error };
  })();

  if (!signal) {
    seatMembersCache.set(teamId, { status: 'loading', promise });
  }
  return promise;
}
